import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { i18next } from '@yola/ws-sdk';
import elementSettings from 'src/js/modules/element-settings';
import { targetClasses } from 'src/js/modules/element-settings/constants/target-classes';
import {
  flexDirectionValues,
  flexWrapValues,
} from 'src/js/modules/element-settings/constants/target-class-values';
import controlIds from '../constants/control-ids';
import previewGifs from '../constants/preview-gifs';

const {
  components: { ActionRadioGroupControl },
  helpers: { createBatch, combineBatches },
} = elementSettings;

const ORDERS = [
  {
    id: 'regular',
    value: flexDirectionValues.ROW,
    glyph: 'flow-horizontal-right',
    getLabel: () => i18next.t('Regular'),
  },
  {
    id: 'reverse',
    value: flexDirectionValues.ROW_REVERSE,
    glyph: 'flow-horizontal-left',
    getLabel: () => i18next.t('Reverse'),
  },
];

const computeFallbackValue = ({ option }) => option.choices[0].value;

const getWrapValue = (directionValue) =>
  directionValue === flexDirectionValues.ROW_REVERSE
    ? flexWrapValues.WRAP_REVERSE
    : flexWrapValues.WRAP;

function OrderControl({ elementIds, targets, nested = false, onChange }) {
  const handleChange = useCallback(
    ({ controlId, elementIds: ids, targets: directionTargets, value, skipTracking = false }) => {
      const wrapValue = getWrapValue(value);
      const batch = combineBatches([
        createBatch({ elementIds: ids, targets: directionTargets, value }),
        createBatch({
          elementIds: ids,
          targets: [targetClasses.FLEX_WRAP],
          value: wrapValue,
        }),
      ]);

      onChange({
        controlId,
        elementIds: ids,
        targets: directionTargets,
        value,
        skipTracking,
        batch,
      });
    },
    [onChange]
  );

  return (
    <ActionRadioGroupControl
      id={controlIds.COLUMNS_ORDER}
      elementIds={elementIds}
      targets={targets}
      title={i18next.t('Column order')}
      choices={ORDERS.map(({ id, value, glyph, getLabel }) => ({
        id,
        value,
        glyph,
        label: getLabel(),
      }))}
      computeFallbackValue={computeFallbackValue}
      nested={nested}
      popover={{
        content: i18next.t('Flip the order in which your columns appear'),
        imgSrc: previewGifs.COLUMNS_ORDER_GIF,
      }}
      onChange={handleChange}
    />
  );
}

OrderControl.propTypes = {
  elementIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  targets: PropTypes.arrayOf(PropTypes.string).isRequired,
  nested: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

export default OrderControl;
